import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { addMinutes, differenceInMinutes } from "date-fns";
import { updateAppointment } from "app/redux/appointmentsSlice";
import { localDateToUtc } from "app/utils/timezone";
import { calendarDateFromUrl } from "app/utils/formatTime";



export type DraftProduct = {
    productId: number;
    quantity: number;
    product: any;
};
type AppointmentDraft = {
    appointmentId: number;
    appointmentGroupId: number;
    startTime: Date;
    endTime: Date;
    services: any[];
    products: DraftProduct[];
    client: any;
    employee: any;
    notes?: string;
};

export const useUpdateAppointmentNew = ({
                                         selectedAppointmentEvent,
                                            setSelectedAppointmentEvent,
                                            getSchedulesOfEmployees
                                     }: {
    selectedAppointmentEvent: any;
    setSelectedAppointmentEvent:any
    getSchedulesOfEmployees: any;
}) => {
    const dispatch = useDispatch();

    const [draft, setDraft] = useState<AppointmentDraft | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    const [hasEdits, setHasEdits] = useState(false);
    const isEditable = !selectedAppointmentEvent?.sale;

    // Time and provider as last saved, to put back when a move is rejected
    const savedRef = useRef<Pick<AppointmentDraft, "startTime" | "endTime" | "employee"> | null>(null);

    /* ---------------------------------- */
    /* INIT DRAFT                          */
    /* ---------------------------------- */
    useEffect(() => {
        if (!selectedAppointmentEvent?.appointment) return;

        const appt = selectedAppointmentEvent.appointment;

        savedRef.current = {
            startTime: selectedAppointmentEvent.start,
            endTime: selectedAppointmentEvent.end,
            employee: selectedAppointmentEvent.employee,
        };
        setDraft({
            appointmentId: appt.id,
            // Appointments booked without a group have appointmentGroup undefined —
            // the bare .id dereference white-screened the whole portal on click.
            appointmentGroupId: appt.appointmentGroup?.id ?? appt.appointmentGroupId ?? null,
            // Use already-timezone-converted start/end from the calendar event (via toLocalDate in mapper)
            // NOT new Date(appt.startTime) which would use system timezone instead of shop timezone
            startTime: selectedAppointmentEvent.start,
            endTime: selectedAppointmentEvent.end,
            services: appt.services || [],
            // products: appt.products || [],
            products: (appt.products || []).map((p: any) => ({
                productId: p.product.id,
                quantity: p.quantity ?? 1,
                product: p.product,
            })),
            client: selectedAppointmentEvent.client,
            employee: selectedAppointmentEvent.employee,
            notes: appt.notes ?? "",
        });
    }, [selectedAppointmentEvent]);

    /* ---------------------------------- */
    /* INTERNAL SAVE                       */
    /* ---------------------------------- */
    const saveDraft = useCallback(
        async (draftToSave: AppointmentDraft) => {
            // if (!isEditable) return;
            console.log('draft to be updated',draftToSave)


            setIsSaving(true);

            try {
                if (draftToSave.services.length === 0) {
                    return toast.error("At least one service is mandatory");
                }
               await dispatch(
                    updateAppointment({
                        appointmentId: draftToSave.appointmentId,
                        // localDateToUtc converts shop-timezone Date back to UTC ISO string for API
                        startTime: localDateToUtc(draftToSave.startTime),
                        endTime: localDateToUtc(draftToSave.endTime),
                        clientId: draftToSave.client.id,
                        employeeId: draftToSave.employee.id,
                        serviceIds: draftToSave.services.map(s => s.id),
                        products: draftToSave.products.map(p => ({
                            product_id: p.productId,
                            quantity: p.quantity,
                        })),
                        notes: draftToSave.notes
                    }) as any
                ).unwrap();
                savedRef.current = {
                    startTime: draftToSave.startTime,
                    endTime: draftToSave.endTime,
                    employee: draftToSave.employee,
                };

                await getSchedulesOfEmployees({
                    currentDate: calendarDateFromUrl()
                });


                toast.success("Appointment Update Successfully ");
            } catch (e: any) {
                console.error(e);
                // e.g. the new time or provider is already booked: say why and put
                // the old time back, or every later autosave would retry the clash
                toast.error(e?.error || "Failed to update appointment");
                const saved = savedRef.current;
                if (saved) setDraft(prev => (prev ? { ...prev, ...saved } : prev));
            } finally {
                setIsSaving(false);
            }
        },
        [dispatch, isEditable, getSchedulesOfEmployees]
    );


    useEffect(() => {
        if (!draft || !hasEdits) return; // only save after user edits

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            saveDraft(draft);
            setHasEdits(false); // reset after saving
        }, 500);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [draft, saveDraft, hasEdits]);

    /* ---------------------------------- */
    /* MUTATORS (UI CALLS THESE)           */
    /* ---------------------------------- */

    const updateDate = useCallback((dateString: string) => {
        setDraft(prev => {
            if (!prev) return prev;

            const [y, m, d] = dateString.split("-").map(Number);
            const newStart = new Date(prev.startTime);

            newStart.setFullYear(y, m - 1, d);

            const duration = differenceInMinutes(prev.endTime, prev.startTime);
            setHasEdits(true);
            return {
                ...prev,
                startTime: newStart,
                endTime: addMinutes(newStart, duration),
            };
        });
    }, []);

    const updateTime = useCallback((timeString: string) => {
        setDraft(prev => {
            if (!prev) return prev;

            const [h, m] = timeString.split(":").map(Number);
            const newStart = new Date(prev.startTime);

            newStart.setHours(h, m);

            const duration = differenceInMinutes(prev.endTime, prev.startTime);
            setHasEdits(true);

            return {
                ...prev,
                startTime: newStart,
                endTime: addMinutes(newStart, duration),
            };
        });
    }, []);

    const updateServices = useCallback((services: any[]) => {
        setDraft(prev => (prev ? { ...prev, services } : prev));
        setHasEdits(true);

    }, []);

    const updateProducts = useCallback((products: any[]) => {
        setDraft(prev => (prev ? { ...prev, products } : prev));
        setHasEdits(true);

    }, []);


    const removeService = (service: any) => {

        updateServices( (draft?.services && (draft?.services?.length > 0)) ? draft?.services.filter(s => s.id !== service?.id) : []);
        setHasEdits(true);

    };
    // const removeProduct = (product: any) => {
    //     // updateProducts(draft.products.filter(p => p.id !== id));
    //     updateProducts((draft?.products && (draft?.products?.length > 0)) ? draft?.products.filter(s => s.id !== product.id) : []);
    //     setHasEdits(true);
    //
    // };
    const addService = (service: any) => {
        updateServices( draft?.services ? [...draft?.services,service] :[service] );
        setHasEdits(true);

    };
    // const addProduct = (product: any) => {
    //     console.log('product--',product)
    //     updateProducts((draft?.products ? [...draft?.products,product] : [product]));
    //     setHasEdits(true);
    //
    // };
    const addProduct = (product: any) => {
        setDraft(prev => {
            if (!prev) return prev;

            const existing = prev.products.find(
                p => p.productId === product.id
            );

            setHasEdits(true);

            if (existing) {
                if (existing.quantity >= product.stock) return prev;

                return {
                    ...prev,
                    products: prev.products.map(p =>
                        p.productId === product.id
                            ? { ...p, quantity: p.quantity + 1 }
                            : p
                    ),
                };
            }

            return {
                ...prev,
                products: [
                    ...prev.products,
                    { productId: product.id, quantity: 1, product },
                ],
            };
        });
    };
    const removeProduct = (product: any) => {
        setDraft(prev => {
            if (!prev) return prev;

            const existing = prev.products.find(
                p => p.productId === product.id
            );

            if (!existing) return prev;

            setHasEdits(true);

            if (existing.quantity === 1) {
                return {
                    ...prev,
                    products: prev.products.filter(p => p.productId !== product.id),
                };
            }

            return {
                ...prev,
                products: prev.products.map(p =>
                    p.productId === product.id
                        ? { ...p, quantity: p.quantity - 1 }
                        : p
                ),
            };
        });
    };
    const removeWholeProduct = (product: any) => {
        setDraft(prev => {
            if (!prev) return prev;

            const existing = prev.products.find(
                p => p.productId === product.id
            );

            if (!existing) return prev;

            setHasEdits(true);
            return {
                    ...prev,
                    products: prev.products.filter(p => p.productId !== product.id),
            };
        });
    };




    const updateNotes = useCallback((notes: string) => {
        setDraft(prev => (prev ? { ...prev, notes } : prev));
        setHasEdits(true);
    }, []);

    // Move the appointment to another barber, with their own copy of each service.
    const changeEmployee = useCallback((employee: any, services: any[]) => {
        setDraft(prev => (prev ? { ...prev, employee, services } : prev));
        setHasEdits(true);
    }, []);

    return {
        draft,
        isSaving,
        isEditable,

        updateDate,
        updateTime,
        updateServices,
        updateProducts,
        updateNotes,
        changeEmployee,

        removeService,
        removeProduct,
        removeWholeProduct,
        addService,
        addProduct,
    };
};
